// Customer's "My Listings" page — shows their own cars across all statuses
// with at-a-glance moderation state and inline edit/photo/archive actions.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, ChevronRight, Hourglass, CheckCircle2, XCircle, FileEdit, Archive } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => (n || n === 0) ? `₹${n.toLocaleString('en-IN')}` : '—';

const STATUS_META = {
  draft:          { label: 'Draft',        cls: 'bg-slate-700/40 text-slate-200', icon: FileEdit },
  pending_review: { label: 'Pending review', cls: 'bg-amber-500/15 text-amber-200 border border-amber-500/30', icon: Hourglass },
  published:      { label: 'Live',         cls: 'bg-teal-500/15 text-teal-200 border border-teal-500/30', icon: CheckCircle2 },
  rejected:       { label: 'Rejected',     cls: 'bg-rose-500/15 text-rose-200 border border-rose-500/30', icon: XCircle },
  sold:           { label: 'Sold',         cls: 'bg-blue-500/15 text-blue-200 border border-blue-500/30', icon: CheckCircle2 },
  archived:       { label: 'Archived',     cls: 'bg-slate-700/30 text-slate-400', icon: Archive },
};

const MyListings = () => {
  const { authHeader, user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [limits, setLimits] = useState({ max_listings: 3, max_photos: 12 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/marketplace/my/cars`, { headers: authHeader() });
      setItems(data.items || []);
      setLimits({ max_listings: data.max_listings, max_photos: data.max_photos });
    } catch (e) {
      console.error('My listings fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      nav('/login?next=/my-listings', { replace: true });
      return;
    }
    load();
  }, [authLoading, user, load, nav]);

  const archive = async (id) => {
    if (!window.confirm('Archive this listing? It will no longer be visible to buyers.')) return;
    try {
      await axios.delete(`${API}/marketplace/my/cars/${id}`, { headers: authHeader() });
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Could not archive.'); }
  };

  const active = items.filter((c) => !['archived', 'sold', 'rejected'].includes(c.status)).length;
  const slotsLeft = Math.max(0, (limits.max_listings || 3) - active);

  if (authLoading || loading) {
    return <div className="min-h-screen bg-slate-50 pt-32 px-6 text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">My listings</h1>
            <p className="text-sm text-slate-500 mt-1">
              {active} of {limits.max_listings} active · {slotsLeft} slot{slotsLeft === 1 ? '' : 's'} available
            </p>
          </div>
          <Link to="/sell-your-car"
            className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm ${
              slotsLeft > 0 ? 'bg-slate-900 hover:bg-slate-800 text-white' : 'bg-slate-300 text-slate-500 pointer-events-none'
            }`}
            data-testid="new-listing-btn">
            <Plus size={14} /> Sell another car
          </Link>
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900">No listings yet</h3>
            <p className="text-sm text-slate-500 mt-1">Post your first car — it&apos;s free, and Unisol verifies every listing before it goes live.</p>
            <Link to="/sell-your-car" className="mt-5 inline-flex items-center gap-1 text-teal-700 font-semibold hover:underline">
              Get started <ChevronRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.draft;
              const StatusIcon = meta.icon;
              const title = [c.manufacture_year, c.make, c.model].filter(Boolean).join(' ');
              return (
                <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-start" data-testid={`my-car-${c.id}`}>
                  <div className="w-full sm:w-44 aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden shrink-0">
                    {c.cover_photo ? <img src={c.cover_photo} alt={title} className="w-full h-full object-cover" /> :
                      <div className="flex items-center justify-center h-full text-slate-300 text-xs">no photo</div>}
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 leading-tight">{title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{c.variant || ''}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.cls}`}>
                        <StatusIcon size={11} /> {meta.label}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-slate-900 mt-2">{inr(c.asking_price)}</p>
                    {c.status === 'rejected' && c.rejection_reason && (
                      <p className="text-xs text-rose-600 mt-1.5 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                        <b>Admin note:</b> {c.rejection_reason}
                      </p>
                    )}
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <Link to={`/sell-your-car/${c.id}`}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                        data-testid={`edit-my-car-${c.id}`}>
                        <FileEdit size={12} /> Edit
                      </Link>
                      {c.status === 'published' && (
                        <Link to={`/marketplace/${c.id}`} target="_blank" rel="noreferrer"
                          className="bg-teal-50 hover:bg-teal-100 text-teal-800 text-xs font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
                          View live page
                        </Link>
                      )}
                      {c.status !== 'archived' && (
                        <button onClick={() => archive(c.id)}
                          className="bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                          data-testid={`archive-my-car-${c.id}`}>
                          <Archive size={12} /> Archive
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyListings;
